import Highcharts, { SeriesOptionsType } from "highcharts";
import { Dictionary, groupBy, isEqual, round, sum, uniq } from "lodash";
import { useEffect, useState } from "react";
import StoneXChart from "../../../../../StoneX/StoneXChart";
import { FixedCallSeriesOption, VarianceReportConfiguration, VarianceReportRow } from "./Types";

interface FixedOptionCoverageChartProps {
  configuration: VarianceReportConfiguration;
  rows: VarianceReportRow[];
  pathToChart: string[] | undefined;
}


export function FixedOptionCoverageChart(props: FixedOptionCoverageChartProps) {

    const { configuration, rows, pathToChart } = props;

    const [chartOptions, setChartOptions] = useState<Highcharts.Options>({});

    const [rowsToChart, setRowsToChart] = useState<VarianceReportRow[]>(rows);
    
    const gray2 = getComputedStyle(document.documentElement).getPropertyValue('--gray2').trim();

    console.log(configuration.chart);

    const seriesOptionToHighchartsSeries: {[key in FixedCallSeriesOption]: (rowDictionary: Dictionary<VarianceReportRow[]>) => SeriesOptionsType} = {
        underlyingCallHedgedVolume: (rowDictionary: Dictionary<VarianceReportRow[]>) => ({
            name: 'Call Option',
            data: Object.entries(rowDictionary).map(([key, entries]) => sum(entries.map(y => round(y.underlyingCallHedgeVolume! / y.budgetVolume! * 100,0)))),
            type: 'column',
            color: configuration.chart?.fixedValueChart?.series?.underlyingCallHedgedVolume?.color || '#4590DB',
            opacity: configuration.chart?.fixedValueChart?.series?.underlyingCallHedgedVolume?.opacity || 1,
            yAxis: 0,
            index: 0
        } as SeriesOptionsType),
        underlyingFixedHedgeVolume: (rowDictionary: Dictionary<VarianceReportRow[]>) => ({
            name: 'Fixed Price',
            data: Object.entries(rowDictionary).map(([key, entries]) => sum(entries.map(y => round(y.underlyingFixedHedgeVolume! / y.budgetVolume!*100,0)))),
            type: 'column',
            color: configuration.chart?.fixedValueChart?.series?.underlyingFixedHedgeVolume?.color || '#39B6F2',
            opacity: configuration.chart?.fixedValueChart?.series?.underlyingFixedHedgeVolume?.opacity || 1,
            yAxis: 0,
            index: 1
        } as SeriesOptionsType),
        strikePrice: (rowDictionary: Dictionary<VarianceReportRow[]>) => ({
            name: 'Weighted Avg. Call Strike Price',
            data: Object.entries(rowDictionary).map(([key, entries]) => sum(entries.map(y => y.underlyingCallStrikePrice))),
            type: 'line',
            yAxis: 1,
            lineWidth: 0,
            color: configuration.chart?.fixedValueChart?.series?.strikePrice?.color || 'red',
            opacity: configuration.chart?.fixedValueChart?.series?.strikePrice?.opacity || 1,
            marker: {
                symbol: 'dash'
            }
        } as SeriesOptionsType),
        budgetPrice: (rowDictionary: Dictionary<VarianceReportRow[]>) => ({
            name: 'Budget Price',
            data: Object.entries(rowDictionary).map(([key, entries]) => sum(entries.map(y => y.budgetPrice))),
            color: configuration.chart?.fixedValueChart?.series?.budgetPrice?.color || '#cc9502',
            opacity: configuration.chart?.fixedValueChart?.series?.budgetPrice?.opacity || 1,
            type: 'line',
            yAxis: 1,
            lineWidth: 2,
        } as SeriesOptionsType),
        marketPrice: (rowDictionary: Dictionary<VarianceReportRow[]>) => ({
            name: 'Market Price',
            data: Object.entries(rowDictionary).map(([key, entries]) => sum(entries.map(y => y.marketPrice))),
            color: configuration.chart?.fixedValueChart?.series?.marketPrice?.color || '#dc572e',
            opacity: configuration.chart?.fixedValueChart?.series?.marketPrice?.opacity || 1,
            type: 'line',
            yAxis: 1
        } as SeriesOptionsType),
        blendedPrice: (rowDictionary: Dictionary<VarianceReportRow[]>) => ({
            name: 'Weighted Average',
            data: Object.entries(rowDictionary).map(([key, entries]) => sum(entries.map(y => y.underlyingBlendedPrice))),
            color: configuration.chart?.fixedValueChart?.series?.blendedPrice?.color || '#aad100',
            opacity: configuration.chart?.fixedValueChart?.series?.blendedPrice?.opacity || 1,
            type: 'line',
            lineWidth: 2,
            yAxis: 1
        } as SeriesOptionsType),
    }

    function updateChart() {

        const isSameCommodity = uniq(rowsToChart.map(x => x.commodity?.commodityId)).length == 1;
        const commodity = isSameCommodity ? rowsToChart?.[0]?.commodity : undefined;

        const formattedNumber = (number?: number, series?: number) => {
            const decimals = series == 1 ? commodity?.displayPrecision ?? 2 : 0;

            let format = number == undefined
                ? undefined
                : "$" + Math.abs(number).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })

            return format;
        };

        setChartOptions({
            title: {
                text: 'Coverage'
            },
            credits: {
                enabled: false,
            },
            tooltip: {
                useHTML: true, 
                formatter: function() {
                // Get axis format string or function if present
                const axis = this.series.yAxis as Highcharts.Axis;
                const axisOptions = axis && axis.options;
                const labelOptions = axisOptions && typeof axisOptions.labels === 'object' ? axisOptions.labels : undefined;
                const formatString = labelOptions && typeof labelOptions.format === 'string' ? labelOptions.format : undefined;
                const formatFunction = labelOptions && typeof labelOptions.formatter === 'function' ? labelOptions.formatter : undefined;

                let value = this.point.y;
                let formattedValue: string | undefined;

                if (formatFunction) {
                // Use the axis label formatter function
                // Construct a context object similar to what Highcharts provides
                const context: Highcharts.AxisLabelsFormatterContextObject = {
                    axis: axis,
                    chart: axis.chart,
                    isFirst: false,
                    isLast: false,
                    pos: this.point.x,
                    tick: undefined as any,
                    value: value!
                };
                    formattedValue = formatFunction.call(context, context);
                } else if (formatString) {
                    // Use Highcharts format method if format string is present
                    formattedValue = Highcharts.format(formatString, { value });
                } else {
                    // Fallback to formattedNumber function
                    formattedValue = formattedNumber(value, (axisOptions as any)?.index);
                }

                    return `
                        <div style="margin: 10px; font-weight: 700; line-height: 1.5em">
                            ${this.x}
                            <br/>
                ${this.series.name}: ${formattedValue}
                        </div>
                    `;
                  }
            },
            plotOptions: {
                column: {
                    stacking: 'normal',
                    dataLabels: {
                        enabled: true,
                        style: {
                            fontSize: '14px',
                            fontWeight: '700',
                            border: '0px',
                            textOutline: 'none'
                        },
                        formatter: function () {
                            // Only show percent for Call Option and Fixed Price columns
                            return Highcharts.numberFormat(this.y!, 0) + '%';
                        }
                    }
                },
                series: {
                    states: {
                        inactive: {
                          opacity: 1 // Prevent other series from fading
                        },
                        hover: {
                          enabled: true,
                          opacity: 1 // Prevent hovered series from changing opacity
                        }
                    }
                }
            },
            xAxis: {
                categories: getMonths(),
                labels: {
                    style: {
                        color: gray2,
                        fontWeight: '700'
                    }
                }
            },
            yAxis: [{
                allowDecimals: false,
                title: {
                    text: '% Hedge',
                    style: {
                        color: gray2,
                        fontWeight: '700'
                    }
                },
                labels: {
                    style: {
                        color: gray2,
                        fontWeight: '700'
                    },
                    formatter: function () {
                        return Highcharts.numberFormat(Number(this.value), 0) + '%'
                    }
                },
                stackLabels: {
                    enabled: true,
                    formatter: function () {
                        return Highcharts.numberFormat(this.total, 0) + '%'
                    }
                },
                opposite: true
            },
            ...isSameCommodity 
                ? [{   // Secondary yAxis
                    gridLineWidth: 0,
                    title: {
                        text: 'Price',
                        style: {
                            color: gray2,
                            fontWeight: '700'
                        }
                    },
                    labels: {
                        format: '${value:.2f}',
                        style: {
                            color: gray2,
                            fontWeight: '700'
                        }
                    },
                    }]
                : []
            ],
            exporting: {
                buttons: {
                contextButton: {
                    enabled: true,
                    menuItems: [
                        'downloadPNG',
                        'downloadSVG',
                        ],
                    },
                },
            },
            series: isSameCommodity  
                ? getSeries('underlyingFixedHedgeVolume', 'underlyingCallHedgedVolume', 'budgetPrice', 'strikePrice', 'marketPrice', 'blendedPrice')
                : getSeries('underlyingFixedHedgeVolume', 'underlyingCallHedgedVolume') 
        })
    }

    function getMonths() {
        var allMonths = rows.filter(x => !x.isAggregated).map(x => x.budgetDate?.label!);
        return uniq(allMonths);
    }

    function getSeries(...columns: FixedCallSeriesOption[]) {
        const groupedRows = groupBy(rowsToChart, x => x.budgetDate?.label!);
        var series = columns.map(column => seriesOptionToHighchartsSeries[column](groupedRows));
        return series;
    }

    function updateRowsToChart() {
        let filteredRows = rows.filter(x => !x.isAggregated);

        if (pathToChart) {
            // Get all rows where the given path 'pathToChart' is a subset of the row's path
            const pathLength = pathToChart.length;
            filteredRows = filteredRows.filter(x => isEqual(x.path.slice(0, pathLength), pathToChart));
        } 

        setRowsToChart(filteredRows);
    }

    useEffect(updateRowsToChart, [rows, pathToChart]);
    useEffect(updateChart, [rowsToChart]);

    return <StoneXChart height='700px' highcharts={Highcharts} options={chartOptions} />;
}